import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { DashboardTimeSeries } from '../../services/dashboardService';
// Side-effect: pushes the app's theme tokens into Chart.js globals (tick/legend
// text + grid lines) and repaints every chart when the theme toggles, so the
// dashboard charts render correctly in dark mode.
import '../../pages/analytics/chartConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'در انتظار پرداخت',
  processing: 'در حال پردازش',
  partially_delivered: 'تحویل جزئی',
  delivered: 'تحویل‌شده',
  completed: 'تکمیل‌شده',
  cancelled: 'لغو‌شده',
  disputed: 'اختلاف',
  refunded: 'مرجوع‌شده',
};

const STATUS_COLORS: Record<string, string> = {
  pending_payment: '#f59e0b',
  processing: '#3b82f6',
  partially_delivered: '#06b6d4',
  delivered: '#0ea5e9',
  completed: '#22c55e',
  cancelled: '#9ca3af',
  disputed: '#ef4444',
  refunded: '#a855f7',
};

/** YYYY-MM-DD → Persian (Jalali) short label via Intl, no extra deps. */
const faDayLabel = (iso: string) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const faNum = (n: number) => (n ?? 0).toLocaleString('fa-IR');

const baseOptions = (rtl = true): ChartOptions<any> => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: {
    legend: { labels: { font: { family: 'Vazirmatn' }, usePointStyle: true, boxWidth: 8 }, rtl, textDirection: rtl ? 'rtl' : 'ltr' },
    tooltip: {
      rtl,
      bodyFont: { family: 'Vazirmatn' },
      titleFont: { family: 'Vazirmatn' },
      callbacks: {
        label: (ctx: any) => `${ctx.dataset.label}: ${faNum(ctx.parsed.y ?? ctx.parsed)}`,
      },
    },
  },
  scales: {
    x: { ticks: { font: { family: 'Vazirmatn', size: 10 } }, grid: { display: false } },
    y: { ticks: { font: { family: 'Vazirmatn', size: 10 }, callback: (v: any) => faNum(Number(v)) }, beginAtZero: true },
  },
});

interface Props {
  data: DashboardTimeSeries | null;
  loading: boolean;
}

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="admin-card">
    <div className="admin-card-header">
      <h2 style={{ fontSize: '1rem' }}>{title}</h2>
    </div>
    <div className="admin-card-body">
      <div style={{ height: 260 }}>{children}</div>
    </div>
  </div>
);

const DashboardCharts: React.FC<Props> = ({ data, loading }) => {
  const labels = useMemo(() => (data?.series ?? []).map((p) => faDayLabel(p.date)), [data]);

  const salesRevenue = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'فروش (تومان)',
          data: (data?.series ?? []).map((p) => p.sales),
          borderColor: '#B25373',
          backgroundColor: 'rgba(178, 83, 115,0.12)',
          fill: true,
          tension: 0.35,
          yAxisID: 'y',
        },
        {
          label: 'درآمد پلتفرم (تومان)',
          data: (data?.series ?? []).map((p) => p.revenue),
          borderColor: '#22c55e',
          backgroundColor: 'rgba(34,197,94,0.12)',
          fill: true,
          tension: 0.35,
          yAxisID: 'y',
        },
      ],
    }),
    [data, labels]
  );

  const ordersBar = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'تعداد سفارش',
          data: (data?.series ?? []).map((p) => p.orders),
          backgroundColor: '#a855f7',
          borderRadius: 4,
        },
      ],
    }),
    [data, labels]
  );

  const usersLine = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'کاربر جدید',
          data: (data?.series ?? []).map((p) => p.newUsers),
          borderColor: '#0ea5e9',
          backgroundColor: 'rgba(14,165,233,0.12)',
          fill: true,
          tension: 0.35,
        },
      ],
    }),
    [data, labels]
  );

  const statusEntries = Object.entries(data?.ordersByStatus ?? {});
  const statusDoughnut = useMemo(
    () => ({
      labels: statusEntries.map(([k]) => STATUS_LABELS[k] ?? k),
      datasets: [
        {
          data: statusEntries.map(([, v]) => v),
          backgroundColor: statusEntries.map(([k]) => STATUS_COLORS[k] ?? '#9ca3af'),
          borderWidth: 0,
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const doughnutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { family: 'Vazirmatn', size: 10 }, usePointStyle: true, boxWidth: 8 }, rtl: true, textDirection: 'rtl' },
      tooltip: { rtl: true, bodyFont: { family: 'Vazirmatn' }, callbacks: { label: (ctx: any) => `${ctx.label}: ${faNum(ctx.parsed)}` } },
    },
  };

  if (loading && !data) {
    return (
      <div className="content-grid">
        {[0, 1].map((i) => (
          <div key={i} className="admin-card">
            <div className="admin-card-body">
              <div className="stat-card-skeleton" style={{ height: 260, width: '100%' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const hasData = (data?.series ?? []).some((p) => p.sales || p.orders || p.newUsers);
  const hasStatus = statusEntries.length > 0;

  return (
    <>
      <div className="content-grid">
        <ChartCard title="فروش و درآمد پلتفرم">
          {hasData ? <Line data={salesRevenue} options={baseOptions()} /> : <EmptyChart />}
        </ChartCard>
        <ChartCard title="تعداد سفارش‌ها">
          {hasData ? <Bar data={ordersBar} options={baseOptions()} /> : <EmptyChart />}
        </ChartCard>
      </div>
      <div className="content-grid">
        <ChartCard title="کاربران جدید">
          {hasData ? <Line data={usersLine} options={baseOptions()} /> : <EmptyChart />}
        </ChartCard>
        <ChartCard title="سفارش‌ها بر اساس وضعیت">
          {hasStatus ? <Doughnut data={statusDoughnut} options={doughnutOptions} /> : <EmptyChart />}
        </ChartCard>
      </div>
    </>
  );
};

const EmptyChart: React.FC = () => (
  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
    داده‌ای برای نمایش در این بازه وجود ندارد
  </div>
);

export default DashboardCharts;
